import { Badges } from "@common/definitions/badges";
import { Emotes } from "@common/definitions/emotes";
import { Loots } from "@common/definitions/loots";
import { Numeric } from "@common/utils/math";
import type { TranslationManifest } from "../../../../vite/plugins/translations-plugin";
import { GameConsole } from "../../console/gameConsole";
import { defaultClientCVars } from "../../console/variables";
import { applyLanguageLook, FULL_STOPS } from "./languageLook";
import { setPhrasebook, translatePhrases } from "./phrases";
import { type TranslationKeys } from "./typings";

export type TranslationMap = Partial<Record<TranslationKeys, string>> & TranslationManifest & {
    /** the menu's own words by English phrase (phrases.ts), from client/src/translations/phrases/<language>.json */
    readonly phrases?: Readonly<Record<string, string>>
};

/** the language whose words stand in for any another hasn't: English, whatever the page starts in */
const FALLBACK_LANGUAGE = "en";

let defaultLanguage: string;
let selectedLanguage: string;

export const TRANSLATIONS: {
    get defaultLanguage(): string
    readonly translations: Record<string, TranslationMap>
} = {
    get defaultLanguage(): string {
        if (!setup) {
            throw new Error("Translation API not yet setup");
        }

        return defaultLanguage;
    },
    translations: {}
};

let setup = false;

let markReady: () => void = () => undefined;
/**
 * settled once the page's language is in (or loading it failed: English), for what draws before the game is set up
 * (the wallet panel, walletFlow.ts: its first words in the language, 1.04.4)
 */
export const translationsReady: Promise<void> = new Promise(resolve => {
    markReady = resolve;
});

export async function initTranslation(): Promise<void> {
    if (setup) {
        console.error("Translation API already setup");
        return;
    }

    setup = true;
    try {
        await loadTranslation();
    } finally {
        markReady();
    }
}

async function loadTranslation(): Promise<void> {
    // (the words missing from a language are English; a page starts in its site's language: Chinese on the Chinese
    // site, English everywhere else, 1.04.4)
    defaultLanguage = FALLBACK_LANGUAGE;
    const siteLanguage = typeof defaultClientCVars.cv_language === "object"
        ? defaultClientCVars.cv_language.value
        : defaultClientCVars.cv_language;

    selectedLanguage = GameConsole.getBuiltInCVar("cv_language");
    if (selectedLanguage === "hp18") { // the removed joke language: back to the default
        selectedLanguage = siteLanguage;
        GameConsole.setBuiltInCVar("cv_language", selectedLanguage);
    }

    const { manifest, importTranslation } = await import("virtual:translations-manifest") as {
        manifest: Record<string, TranslationManifest>
        importTranslation: (t: string) => Promise<TranslationMap>
    };
    // a language this version doesn't have (typed in the console, or kept from an older one: 1.04.4 kept five of
    // twenty-seven): the site's (an unknown one stopped the game as it started, with no way to reset it but clearing
    // the site's data by hand)
    // (its own languages only: "constructor" is in every object)
    if (!Object.hasOwn(manifest, selectedLanguage)) {
        selectedLanguage = siteLanguage in manifest ? siteLanguage : defaultLanguage;
        GameConsole.setBuiltInCVar("cv_language", selectedLanguage);
    }

    for (
        const [language, content] of await Promise.all(
            Object.entries(manifest)
                .map(async([language, content]): Promise<[string, TranslationMap]> => [
                    language,
                    content.mandatory || language === selectedLanguage || language === defaultLanguage
                        ? await importTranslation(language)
                        : content
                ])
        )
    ) {
        TRANSLATIONS.translations[language] = {
            ...manifest[language],
            ...content
        };
    }

    // the menu's own words (phrases.ts): the language picked's phrasebook, English's being the phrases themselves
    const picked = TRANSLATIONS.translations[selectedLanguage];
    setPhrasebook(selectedLanguage === FALLBACK_LANGUAGE ? undefined : picked?.phrases, { noSpace: picked?.no_space === true, fullStop: FULL_STOPS[selectedLanguage] });

    translateCurrentDOM();
}

/** the language the page is in (its code: "en", "es", "br", "fr", "zh") */
export const selectedLanguageCode = (): string => selectedLanguage;

/** whether the language picked has its own words for `key` (not the default language's, put in for want of them) */
export function hasOwnTranslation(key: TranslationKeys): boolean {
    return setup && TRANSLATIONS.translations[selectedLanguage]?.[key] !== undefined;
}

export function translate(key: TranslationKeys, replacements?: Record<string, string>): string {
    if (!setup) {
        console.error("Translation API not yet setup");
        return key;
    }

    if (key.startsWith("badge_")) {
        key = Badges.reify(key.slice("badge_".length)).idString.replace("bdg_", "badge_") as TranslationKeys;
    }

    const languageData = TRANSLATIONS.translations[selectedLanguage];
    const defaultLanguageData = TRANSLATIONS.translations[defaultLanguage];

    if (!languageData) {
        console.error(`Language ${selectedLanguage} does not exist`);
        return key;
    }

    let foundTranslation: string | undefined;
    foundTranslation = languageData[key];
    foundTranslation ??= defaultLanguageData[key];
    foundTranslation ??= Loots.fromStringSafe(key)?.name;

    foundTranslation ??= key.startsWith("emote_") ? Emotes.fromStringSafe(key.slice("emote_".length))?.name : undefined;
    foundTranslation ??= key.startsWith("bdg_") ? Badges.fromStringSafe(key)?.name : undefined;

    foundTranslation ??= key;

    for (const [search, replace] of Object.entries(replacements ?? {})) {
        foundTranslation = foundTranslation.split(`<${search}>`).join(replace);
    }

    return foundTranslation;
}

const printTranslationDebug = false;

function adjustFontSize(element: HTMLElement): void {
    if (!element.textContent) return;

    const MIN_FONT_SIZE = element.parentElement?.classList.contains("tab") ? 12.5 : 15;
    const FONT_WIDTH_PER_CHARACTER = 10;

    // I love math
    const buttonText = element.textContent.replace(/\s+/g, " ");
    const textWidth = buttonText.length * FONT_WIDTH_PER_CHARACTER;
    const buttonWidth = element.getBoundingClientRect().width;

    // (Tamil's smaller ceiling went with its file, 1.04.4)
    const fontSize = Numeric.clamp(
        buttonWidth / textWidth * FONT_WIDTH_PER_CHARACTER,
        MIN_FONT_SIZE - 2,
        20
    );

    element.style.fontSize = `${fontSize}px`;
    element.style.verticalAlign = "middle";
}

/**
 * the page's language code (what a screen reader reads it in) for the languages whose file doesn't give one
 * (`html_lang`): the others go by their own key ("de", "fr"). English had none, so the page's lang="en" was wiped to
 * "" for nearly everyone
 */
const HTML_LANG: Readonly<Record<string, string>> = { en: "en", br: "pt-BR" };

function translateCurrentDOM(): void {
    let debugTranslationCounter = 0;
    document.documentElement.lang = TRANSLATIONS.translations[selectedLanguage]?.html_lang ?? HTML_LANG[selectedLanguage] ?? selectedLanguage;
    // (Arabic's words right to left in their boxes, the menu where it is; Arabic's and Hindi's letters unspaced: 1.04.5)
    applyLanguageLook(selectedLanguage, false);
    // (the page's own words: each element marked t or t-attr, phrases.ts)
    translatePhrases(document);
    document.querySelectorAll("body *").forEach(element => {
        if (!(element instanceof HTMLElement)) return; // ignore non-html elements (like svg and mathml)

        const requestedTranslation = element.getAttribute("translation");
        if (!requestedTranslation) return;

        const translatedString = translate(requestedTranslation as TranslationKeys); // We pray

        element[
            element.getAttribute("use-html") === null
                ? "innerText"
                : "innerHTML"
        ] = translatedString;

        // Decrease font size for those languages have have really long stuff in buttons
        if (
            (element.classList.contains("btn") || element.parentElement?.classList.contains("btn") || element.parentElement?.classList.contains("tab"))
            && translatedString.length >= 10
            && !TRANSLATIONS.translations[selectedLanguage]?.no_resize
        ) {
            adjustFontSize(element);
        }

        debugTranslationCounter++;
    });

    if (printTranslationDebug) {
        console.log("Translated", debugTranslationCounter, "strings");
        console.log("With language as", selectedLanguage, "and default as", defaultLanguage);

        const reference = new Set(Object.keys(TRANSLATIONS.translations[TRANSLATIONS.defaultLanguage]));

        console.table(
            Array.from(Object.entries(TRANSLATIONS.translations)).reduce<{
                [K in keyof typeof TRANSLATIONS.translations]: {
                    readonly "translation coverage (%)": number
                    readonly "missing keys": readonly string[]
                }
            }>(
                (acc, [language, languageInfo]) => {
                    const copy = new Set(reference);

                    for (const key of Object.keys(languageInfo)) {
                        copy.delete(key);
                    }

                    acc[language] = {
                        "translation coverage (%)": 100 * (1 - copy.size / reference.size),
                        "missing keys": Array.from(copy)
                    };

                    return acc;
                },
                {}
            )
        );
    }
}
